import { FC, PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from 'react';

import {
  ThanksPrintBox,
  ThanksPrintTextAlign,
} from '@shared/api/giftItemBrand/giftItemBrand.interface';

export type ThanksPrintBoxKey = 'message' | 'sender';

interface ThanksPrintBoxEditorProps {
  /** 템플릿 이미지 URL (object URL 또는 버킷 URL) */
  imageUrl: string;
  messageBox?: ThanksPrintBox;
  senderBox?: ThanksPrintBox;
  /**
   * 박스 좌표(0~1) 변경 콜백. undefined = 영역 제거
   * (감사글 영역을 제거하면 "문구가 이미 인쇄된 카드"로 동작 — 구매자는 이름만 입력)
   */
  onChange: (key: ThanksPrintBoxKey, box: ThanksPrintBox | undefined) => void;
}

type Corner = 'nw' | 'ne' | 'sw' | 'se';

type DragState =
  | { type: 'move'; key: ThanksPrintBoxKey; startX: number; startY: number; orig: ThanksPrintBox }
  | { type: 'resize'; key: ThanksPrintBoxKey; corner: Corner; orig: ThanksPrintBox }
  | { type: 'draw'; key: ThanksPrintBoxKey; anchorX: number; anchorY: number };

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/** x0<x1, y0<y1 이 되도록 정렬 (align·colorHex·underline 등 스타일 필드는 보존) */
const normalizeBox = (box: ThanksPrintBox): ThanksPrintBox => ({
  ...box,
  x0: Math.min(box.x0, box.x1),
  y0: Math.min(box.y0, box.y1),
  x1: Math.max(box.x0, box.x1),
  y1: Math.max(box.y0, box.y1),
});

const DEFAULT_BOX: Record<ThanksPrintBoxKey, ThanksPrintBox> = {
  message: { x0: 0.15, y0: 0.2, x1: 0.85, y1: 0.5 },
  sender: { x0: 0.55, y0: 0.6, x1: 0.9, y1: 0.72 },
};

const BOX_META: Record<
  ThanksPrintBoxKey,
  { label: string; border: string; bg: string; badge: string; handle: string }
> = {
  message: {
    label: '감사글',
    border: 'border-indigo-500',
    bg: 'bg-indigo-500/15',
    badge: 'bg-indigo-500',
    handle: 'bg-indigo-500',
  },
  sender: {
    label: '상주명',
    border: 'border-rose-500',
    bg: 'bg-rose-500/15',
    badge: 'bg-rose-500',
    handle: 'bg-rose-500',
  },
};

const CORNERS: { corner: Corner; pos: string }[] = [
  { corner: 'nw', pos: '-left-1.5 -top-1.5 cursor-nwse-resize' },
  { corner: 'ne', pos: '-right-1.5 -top-1.5 cursor-nesw-resize' },
  { corner: 'sw', pos: '-left-1.5 -bottom-1.5 cursor-nesw-resize' },
  { corner: 'se', pos: '-right-1.5 -bottom-1.5 cursor-nwse-resize' },
];

type CoordField = 'x0' | 'y0' | 'x1' | 'y1';

const COORD_FIELDS: { field: CoordField; label: string }[] = [
  { field: 'x0', label: '좌 X' },
  { field: 'y0', label: '상 Y' },
  { field: 'x1', label: '우 X' },
  { field: 'y1', label: '하 Y' },
];

/** 0~1 비율 → % 문자열 (소수 1자리, 불필요한 0 제거) */
const toPercent = (v: number) => (Math.round(v * 1000) / 10).toString();

/** colorHex 미설정 시 미리보기 기본 글자색 (text-gray-900) — 색상 피커 표시용 */
const DEFAULT_TEXT_COLOR = '#111827';

export const ThanksPrintBoxEditor: FC<ThanksPrintBoxEditorProps> =
  function ThanksPrintBoxEditor({ imageUrl, messageBox, senderBox, onChange }) {
    const containerRef = useRef<HTMLDivElement>(null);
    const dragRef = useRef<DragState | null>(null);
    const [activeKey, setActiveKey] = useState<ThanksPrintBoxKey>('message');

    // 좌표 직접 입력 중인 필드 — 입력 중에는 드래그/정규화 값으로 표시를 덮지 않는다
    const [coordDraft, setCoordDraft] = useState<{
      field: CoordField;
      value: string;
    } | null>(null);
    useEffect(() => setCoordDraft(null), [activeKey]);

    const boxes: Record<ThanksPrintBoxKey, ThanksPrintBox | undefined> = {
      message: messageBox,
      sender: senderBox,
    };

    // 좌표 변경 시 스타일 필드(align·colorHex·underline) 유지 — 기존 박스 위에 좌표만 덮는다
    const emit = (key: ThanksPrintBoxKey, coords: ThanksPrintBox) => {
      onChange(key, {
        ...boxes[key],
        ...coords,
        align: coords.align ?? boxes[key]?.align ?? ThanksPrintTextAlign.Center,
      });
    };

    // 포인터 위치를 컨테이너 기준 0~1 비율로 변환
    const toNorm = (e: ReactPointerEvent) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };
      return {
        x: clamp01((e.clientX - rect.left) / rect.width),
        y: clamp01((e.clientY - rect.top) / rect.height),
      };
    };

    // 빈 영역 클릭 → 활성 박스 새로 그리기 시작
    const onCanvasPointerDown = (e: ReactPointerEvent) => {
      // 박스/핸들에서 시작한 경우는 각자 핸들러가 처리(stopPropagation)
      const { x, y } = toNorm(e);
      containerRef.current?.setPointerCapture(e.pointerId);
      dragRef.current = { type: 'draw', key: activeKey, anchorX: x, anchorY: y };
      emit(activeKey, { x0: x, y0: y, x1: x, y1: y });
    };

    const onBoxPointerDown = (e: ReactPointerEvent, key: ThanksPrintBoxKey) => {
      e.stopPropagation();
      const box = boxes[key];
      if (!box) return;
      setActiveKey(key);
      const { x, y } = toNorm(e);
      containerRef.current?.setPointerCapture(e.pointerId);
      dragRef.current = {
        type: 'move',
        key,
        startX: x,
        startY: y,
        orig: normalizeBox(box),
      };
    };

    const onHandlePointerDown = (
      e: ReactPointerEvent,
      key: ThanksPrintBoxKey,
      corner: Corner,
    ) => {
      e.stopPropagation();
      const box = boxes[key];
      if (!box) return;
      setActiveKey(key);
      containerRef.current?.setPointerCapture(e.pointerId);
      dragRef.current = { type: 'resize', key, corner, orig: normalizeBox(box) };
    };

    const onPointerMove = (e: ReactPointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const { x, y } = toNorm(e);

      if (drag.type === 'draw') {
        emit(drag.key, { x0: drag.anchorX, y0: drag.anchorY, x1: x, y1: y });
        return;
      }

      if (drag.type === 'move') {
        const w = drag.orig.x1 - drag.orig.x0;
        const h = drag.orig.y1 - drag.orig.y0;
        const dx = x - drag.startX;
        const dy = y - drag.startY;
        const nx0 = Math.min(Math.max(drag.orig.x0 + dx, 0), 1 - w);
        const ny0 = Math.min(Math.max(drag.orig.y0 + dy, 0), 1 - h);
        emit(drag.key, { x0: nx0, y0: ny0, x1: nx0 + w, y1: ny0 + h });
        return;
      }

      // resize: 잡은 코너만 이동, 반대 코너 고정 (스타일 필드 보존)
      const o = drag.orig;
      const left = drag.corner === 'nw' || drag.corner === 'sw' ? x : o.x0;
      const right = drag.corner === 'ne' || drag.corner === 'se' ? x : o.x1;
      const top = drag.corner === 'nw' || drag.corner === 'ne' ? y : o.y0;
      const bottom = drag.corner === 'sw' || drag.corner === 'se' ? y : o.y1;
      onChange(drag.key, { ...o, x0: left, y0: top, x1: right, y1: bottom });
    };

    const onPointerUp = (e: ReactPointerEvent) => {
      const drag = dragRef.current;
      if (drag) {
        const box = boxes[drag.key];
        if (box) emit(drag.key, normalizeBox(box));
      }
      dragRef.current = null;
      containerRef.current?.releasePointerCapture(e.pointerId);
    };

    // 좌표 숫자 직접 입력 (% → 0~1). 파싱 가능한 값이면 즉시 반영해 오버레이가 따라 움직인다
    const onCoordInput = (field: CoordField, value: string) => {
      setCoordDraft({ field, value });
      const box = boxes[activeKey];
      if (!box) return;
      const n = Number(value);
      if (!Number.isFinite(n)) return;
      emit(activeKey, { ...box, [field]: clamp01(n / 100) });
    };

    // 입력 종료 시 x0<x1, y0<y1 정규화
    const onCoordBlur = () => {
      setCoordDraft(null);
      const box = boxes[activeKey];
      if (box) emit(activeKey, normalizeBox(box));
    };

    const renderBox = (key: ThanksPrintBoxKey) => {
      const raw = boxes[key];
      if (!raw) return null;
      const box = normalizeBox(raw);
      const meta = BOX_META[key];
      const isActive = key === activeKey;
      return (
        <div
          key={key}
          onPointerDown={(e) => onBoxPointerDown(e, key)}
          className={`absolute border-2 ${meta.border} ${meta.bg} ${
            isActive ? 'z-20' : 'z-10 opacity-60'
          }`}
          style={{
            left: `${box.x0 * 100}%`,
            top: `${box.y0 * 100}%`,
            width: `${(box.x1 - box.x0) * 100}%`,
            height: `${(box.y1 - box.y0) * 100}%`,
            cursor: 'move',
          }}
        >
          <span
            className={`absolute -top-5 left-0 whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-bold text-white ${meta.badge}`}
          >
            {meta.label}
          </span>
          {isActive &&
            CORNERS.map(({ corner, pos }) => (
              <div
                key={corner}
                onPointerDown={(e) => onHandlePointerDown(e, key, corner)}
                className={`absolute h-3 w-3 rounded-full border-2 border-white ${meta.handle} ${pos}`}
              />
            ))}
        </div>
      );
    };

    return (
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        {/* 이미지 + 박스 오버레이 (왼쪽, 적당한 크기로 제한) */}
        <div
          ref={containerRef}
          onPointerDown={onCanvasPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          className="relative w-full shrink-0 touch-none select-none overflow-hidden rounded-lg border border-gray-200 bg-gray-50 sm:w-80"
        >
          <img
            src={imageUrl}
            alt="감사인쇄 템플릿"
            draggable={false}
            className="pointer-events-none w-full select-none"
          />
          {renderBox('sender')}
          {renderBox('message')}
        </div>

        {/* 컨트롤 (오른쪽, 남는 공간 활용) */}
        <div className="min-w-0 flex-1 space-y-3">
          {/* 박스 선택 탭 */}
          <div className="flex flex-wrap gap-2">
            {(['message', 'sender'] as ThanksPrintBoxKey[]).map((key) => {
              const meta = BOX_META[key];
              const isActive = key === activeKey;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveKey(key)}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${
                    isActive
                      ? `${meta.border} ${meta.badge} text-white`
                      : 'border-gray-300 bg-white text-gray-600'
                  }`}
                >
                  <span
                    className={`h-2.5 w-2.5 rounded-full ${
                      isActive ? 'bg-white' : meta.badge
                    }`}
                  />
                  {meta.label} 영역
                  {!boxes[key] && <span className="opacity-70">(미설정)</span>}
                </button>
              );
            })}
          </div>

          {/* 활성 박스 정렬 */}
          {boxes[activeKey] && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">정렬</span>
              <div className="flex overflow-hidden rounded-md border border-gray-300">
                {(
                  [
                    { value: ThanksPrintTextAlign.Left, label: '왼쪽' },
                    { value: ThanksPrintTextAlign.Center, label: '가운데' },
                    { value: ThanksPrintTextAlign.Right, label: '오른쪽' },
                  ] as const
                ).map(({ value, label }) => {
                  const current = boxes[activeKey]?.align ?? ThanksPrintTextAlign.Center;
                  const isActive = current === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => {
                        const box = boxes[activeKey];
                        if (box) onChange(activeKey, { ...box, align: value });
                      }}
                      className={`px-2.5 py-1 text-xs font-medium ${
                        isActive ? 'bg-gray-800 text-white' : 'bg-white text-gray-600'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* 활성 박스 좌표 — 값 표시 + 숫자 직접 입력 (이미지 크기 대비 %) */}
          {boxes[activeKey] && (
            <div>
              <span className="mb-1 block text-xs text-gray-500">
                좌표 (이미지 크기 대비 %, 0~100)
              </span>
              <div className="grid grid-cols-4 gap-1.5">
                {COORD_FIELDS.map(({ field, label }) => {
                  const box = boxes[activeKey];
                  const shown =
                    coordDraft?.field === field
                      ? coordDraft.value
                      : box
                      ? toPercent(box[field])
                      : '';
                  return (
                    <label key={field} className="block">
                      <span className="mb-0.5 block text-[10px] text-gray-400">
                        {label}
                      </span>
                      <input
                        type="number"
                        value={shown}
                        onChange={(e) => onCoordInput(field, e.target.value)}
                        onFocus={() => {
                          const b = boxes[activeKey];
                          if (b) setCoordDraft({ field, value: toPercent(b[field]) });
                        }}
                        onBlur={onCoordBlur}
                        min={0}
                        max={100}
                        step={0.1}
                        className="w-full rounded-md border border-gray-300 px-1.5 py-1 text-xs"
                      />
                    </label>
                  );
                })}
              </div>
              {(() => {
                const box = boxes[activeKey];
                if (!box) return null;
                const n = normalizeBox(box);
                return (
                  <p className="mt-1 text-[10px] text-gray-400">
                    크기 {toPercent(n.x1 - n.x0)} × {toPercent(n.y1 - n.y0)}% — 숫자를
                    직접 입력해 미세 조정할 수 있어요.
                  </p>
                );
              })()}
            </div>
          )}

          {/* 활성 박스 글자 스타일 (색상 / 배상 밑줄) */}
          {boxes[activeKey] && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500">글자색</span>
                <input
                  type="color"
                  value={boxes[activeKey]?.colorHex ?? DEFAULT_TEXT_COLOR}
                  onChange={(e) => {
                    const box = boxes[activeKey];
                    if (box) onChange(activeKey, { ...box, colorHex: e.target.value });
                  }}
                  className="h-6 w-8 cursor-pointer rounded border border-gray-300 p-0"
                />
                <span className="font-mono text-[10px] text-gray-400">
                  {boxes[activeKey]?.colorHex ?? '기본색'}
                </span>
                {boxes[activeKey]?.colorHex && (
                  <button
                    type="button"
                    onClick={() => {
                      const box = boxes[activeKey];
                      if (!box) return;
                      const rest = { ...box };
                      delete rest.colorHex;
                      onChange(activeKey, rest);
                    }}
                    className="rounded border border-gray-300 px-1.5 py-0.5 text-[10px] text-gray-500 hover:bg-gray-50"
                  >
                    기본색으로
                  </button>
                )}
              </div>
              {activeKey === 'sender' && (
                <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-500">
                  <input
                    type="checkbox"
                    checked={boxes.sender?.underline !== false}
                    onChange={(e) => {
                      const box = boxes.sender;
                      if (box)
                        onChange('sender', { ...box, underline: e.target.checked });
                    }}
                    className="h-4 w-4 accent-rose-500"
                  />
                  &quot;OOO 배상&quot; 밑줄
                </label>
              )}
            </div>
          )}

          {/* 안내 + 기본 위치 / 영역 제거 */}
          <div className="flex items-start justify-between gap-2">
            <p className="text-xs text-gray-500">
              이미지 위에서 <b>드래그</b>해 <b>{BOX_META[activeKey].label}</b> 영역을
              그리고, 모서리 점으로 크기를 조절하세요.
            </p>
            <div className="flex shrink-0 gap-1.5">
              {boxes[activeKey] && (
                <button
                  type="button"
                  onClick={() => {
                    setCoordDraft(null);
                    onChange(activeKey, undefined);
                  }}
                  className="rounded-md border border-red-300 px-2 py-1 text-xs text-red-500 hover:bg-red-50"
                >
                  영역 지정 안 함
                </button>
              )}
              <button
                type="button"
                onClick={() => emit(activeKey, DEFAULT_BOX[activeKey])}
                className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
              >
                기본 위치
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };
